import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ENV } from "@/lib/env";
import { supabase, currentUser } from "@/lib/supabase";

const CF_PURPLE = "#C840E9";
const IPAI_GREEN = "#a78bfa";
const APP_STORE_URL = "https://apps.apple.com/us/app/input-performance-ai/id6758412527";

function isEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/* ─── About You Phone Mockup ─── */
function AboutYouMockup() {
  return (
    <div className="relative mx-auto w-[280px] sm:w-[320px]">
      {/* Phone frame */}
      <div className="rounded-[2.5rem] border-[3px] border-[#e2e8f0] bg-[#e2e8f0] p-3 shadow-2xl shadow-black/60">
        {/* Notch */}
        <div className="mx-auto mb-3 h-5 w-28 rounded-full bg-[#f1f5f9]" />

        {/* Screen content */}
        <div className="rounded-2xl bg-[#ffffff] px-4 pb-5 pt-3">
          {/* Back + title */}
          <p className="text-[11px] text-gray-600 mb-3">&larr; Back</p>
          <h3 className="text-center text-base font-bold text-[#0f172a] mb-0.5">About You</h3>
          <p className="text-center text-[10px] text-gray-500 mb-4">
            We'll use this to personalise your plan
          </p>

          {/* Form card */}
          <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-3 space-y-3">
            {/* Name */}
            <div>
              <p className="text-[10px] text-gray-500 mb-1">Name</p>
              <div className="h-8 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0] px-2 flex items-center">
                <span className="text-[11px] text-gray-600">Your name</span>
              </div>
            </div>

            {/* DOB + Gender row */}
            <div className="flex gap-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-500 mb-1">
                  DOB <span className="text-gray-600">(Optional)</span>
                </p>
                <div className="h-8 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0] px-2 flex items-center">
                  <span className="text-[11px] text-gray-600">DD/MM/YYYY</span>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-gray-500 mb-1">
                  Gender <span className="text-gray-600">(Optional)</span>
                </p>
                <div className="flex gap-1">
                  <div className="flex-1 h-8 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0] flex items-center justify-center">
                    <span className="text-[10px] text-gray-500">Male</span>
                  </div>
                  <div className="flex-1 h-8 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0] flex items-center justify-center">
                    <span className="text-[10px] text-gray-500">Female</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Height + Weight row */}
            <div className="flex gap-2">
              <div className="flex-1">
                <p className="text-[10px] text-gray-500 mb-1">Height</p>
                <div className="flex gap-1">
                  <div className="flex-1 h-8 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0] px-2 flex items-center">
                    <span className="text-[11px] text-gray-600">180</span>
                  </div>
                  <div className="h-8 w-9 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0] flex items-center justify-center">
                    <span className="text-[10px] text-[#0f172a]">cm</span>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-gray-500 mb-1">Weight (kg)</p>
                <div className="h-8 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0] px-2 flex items-center">
                  <span className="text-[11px] text-gray-600">75</span>
                </div>
              </div>
            </div>

            {/* Activity Level */}
            <div>
              <p className="text-[10px] text-gray-500 mb-1">Activity Level</p>
              <div className="flex flex-wrap gap-1">
                {["Sedentary", "Light", "Moderate", "Active", "Athlete"].map((l, i) => (
                  <div
                    key={l}
                    className={`rounded-lg px-2 py-1.5 text-center ${
                      i === 3
                        ? "bg-[#a78bfa] border border-[#a78bfa]"
                        : "bg-[#f1f5f9] border border-[#e2e8f0]"
                    }`}
                    style={{ flex: i < 3 ? "1 1 30%" : "1 1 45%" }}
                  >
                    <span className={`text-[9px] font-medium ${i === 3 ? "text-[#0f172a]" : "text-gray-500"}`}>
                      {l}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Continue button */}
            <div className="h-9 rounded-lg bg-[#a78bfa] flex items-center justify-center mt-1">
              <span className="text-xs font-bold text-[#0f172a]">Continue</span>
            </div>
          </div>

          {/* Step dots */}
          <div className="flex items-center justify-center gap-1.5 mt-4">
            <div className="h-1.5 w-6 rounded-full bg-[#a78bfa]" />
            <div className="h-1.5 w-6 rounded-full bg-[#a78bfa]/40" />
            <div className="h-1.5 w-2.5 rounded-full bg-[#f1f5f9]" />
            <div className="h-1.5 w-2.5 rounded-full bg-[#f1f5f9]" />
            <div className="h-1.5 w-2.5 rounded-full bg-[#f1f5f9]" />
            <div className="h-1.5 w-2.5 rounded-full bg-[#f1f5f9]" />
          </div>
        </div>
      </div>
    </div>
  );
}

const ACTIVITY_LEVELS = [
  { value: "sedentary", label: "Sedentary", desc: "Little or no exercise" },
  { value: "lightly active", label: "Light", desc: "1-2 days/week" },
  { value: "moderately active", label: "Moderate", desc: "3-5 days/week" },
  { value: "very active", label: "Active", desc: "6-7 days/week" },
  { value: "extremely active", label: "Athlete", desc: "2x daily" },
];

function CrossfitWessexOnboarding() {
  const [profileSaved, setProfileSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    dob: "",
    gender: "",
    height: "",
    heightUnit: "cm",
    weight: "",
    activityLevel: "",
  });

  const calculateAge = (dob: string) => {
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const maxDob = new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split("T")[0];

  const isFormValid = () => {
    return !!(form.name && form.dob && form.gender && form.height && form.weight && form.activityLevel);
  };

  const handleSaveProfile = async () => {
    setFormErr(null);
    setSaving(true);

    try {
      const user = await currentUser();
      if (!user) throw new Error("Not logged in. Please go back and verify your email.");

      const age = calculateAge(form.dob);
      if (age < 16) {
        setFormErr("You must be at least 16 years old.");
        setSaving(false);
        return;
      }

      const heightCm = form.heightUnit === "in"
        ? Number(form.height) * 2.54
        : Number(form.height);

      const now = new Date().toISOString();
      const expiresAt = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();

      const { error } = await supabase.from("clients").upsert(
        {
          user_id: user.id,
          email_address: user.email,
          name: form.name.trim(),
          date_of_birth: form.dob,
          age,
          height_in_cm: heightCm || null,
          weight_in_kg: Number(form.weight) || null,
          gender: form.gender.toLowerCase(),
          activity_level: form.activityLevel,
          subscription_status: "active",
          subscription_provider: "promo",
          subscription_plan: "crossfit-wessex",
          subscription_started_at: now,
          subscription_expires_at: expiresAt,
          onboarded: false,
        },
        { onConflict: "user_id" }
      );

      if (error) throw error;
      setProfileSaved(true);
    } catch (e: any) {
      setFormErr(e.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const cfwHeader = (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#e2e8f0]">
      <div className="flex items-center justify-center px-4 py-3 max-w-screen-xl mx-auto gap-3">
        <div className="w-7 h-7 rounded-full bg-[#a78bfa] flex items-center justify-center">
          <svg className="w-4 h-4 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span className="font-bold text-[#0f172a] text-sm">Input</span>
        <span className="text-gray-600 text-lg font-light">x</span>
        <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-[8px] text-[#0f172a]" style={{ background: CF_PURPLE }}>CFW</div>
        <span className="font-bold text-[#0f172a] text-sm">CrossFit Wessex</span>
      </div>
    </header>
  );

  // ─── Success / Download screen ───
  if (profileSaved) {
    return (
      <div className="min-h-screen bg-[#ffffff] text-[#0f172a] flex flex-col">
        {cfwHeader}
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: `${IPAI_GREEN}15` }}>
              <svg className="w-10 h-10" style={{ color: IPAI_GREEN }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-extrabold mb-3">You're all set, {form.name.split(" ")[0]}!</h1>
            <p className="text-gray-500 text-lg mb-2">
              Your <strong className="text-[#0f172a]">2 months free access</strong> is activated.
            </p>
            <p className="text-gray-500 mb-10">
              Download the app and sign in with your email to start training.
            </p>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-base text-[#0f172a] transition hover:opacity-90 w-full justify-center mb-4"
              style={{ background: CF_PURPLE }}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              Download on the App Store
            </a>
            <p className="text-gray-600 text-sm">
              Sign in with the same email you used to sign up.
            </p>
          </div>
        </main>
        <footer className="py-6 text-center border-t border-[#e2e8f0]">
          <p className="text-gray-600 text-xs">&copy; {new Date().getFullYear()} Input. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  // ─── About You form ───
  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a] flex flex-col">
      {cfwHeader}

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">About You</h1>
            <p className="text-gray-500 text-sm">We'll use this to personalise your calories and macros</p>
          </div>

          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5">
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Name</label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#C840E9]/50 transition-all"
                />
              </div>

              {/* DOB + Gender */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    value={form.dob}
                    onChange={(e) => setForm(f => ({ ...f, dob: e.target.value }))}
                    max={maxDob}
                    className="w-full h-12 px-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] focus:outline-none focus:border-[#C840E9]/50 transition-all [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Gender</label>
                  <div className="grid grid-cols-2 gap-2">
                    {["Male", "Female"].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, gender: g }))}
                        className={`h-12 rounded-xl font-medium text-sm transition-all duration-200 ${
                          form.gender === g
                            ? "text-[#0f172a]"
                            : "bg-[#f1f5f9] text-gray-500 border border-[#e2e8f0] hover:border-[#e2e8f0]"
                        }`}
                        style={form.gender === g ? { background: CF_PURPLE } : undefined}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Height + Weight */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Height</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="180"
                      value={form.height}
                      onChange={(e) => setForm(f => ({ ...f, height: e.target.value }))}
                      className="flex-1 h-12 px-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#C840E9]/50 transition-all"
                    />
                    <select
                      value={form.heightUnit}
                      onChange={(e) => setForm(f => ({ ...f, heightUnit: e.target.value }))}
                      className="w-16 h-12 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-2 text-[#0f172a] text-sm focus:outline-none focus:border-[#C840E9]/50 transition-all"
                    >
                      <option value="cm">cm</option>
                      <option value="in">in</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Weight (kg)</label>
                  <input
                    type="number"
                    placeholder="75"
                    value={form.weight}
                    onChange={(e) => setForm(f => ({ ...f, weight: e.target.value }))}
                    className="w-full h-12 px-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#C840E9]/50 transition-all"
                  />
                </div>
              </div>

              {/* Activity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-3">Activity Level</label>
                <div className="grid grid-cols-5 gap-2">
                  {ACTIVITY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, activityLevel: level.value }))}
                      className={`p-2 rounded-xl text-center transition-all duration-200 ${
                        form.activityLevel === level.value
                          ? "text-[#0f172a]"
                          : "bg-[#f1f5f9] text-gray-500 border border-[#e2e8f0] hover:border-[#e2e8f0]"
                      }`}
                      style={form.activityLevel === level.value ? { background: CF_PURPLE } : undefined}
                    >
                      <p className="font-medium text-xs">{level.label}</p>
                      <p className={`text-[8px] mt-0.5 leading-tight ${
                        form.activityLevel === level.value ? "text-[#0f172a]/60" : "text-gray-600"
                      }`}>
                        {level.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveProfile}
              disabled={saving || !isFormValid()}
              className="w-full h-12 font-semibold text-base rounded-xl transition-all duration-200 disabled:bg-[#f1f5f9] disabled:text-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-[#0f172a] mt-6"
              style={!saving && isFormValid() ? { background: CF_PURPLE } : undefined}
            >
              {saving ? (
                <>
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </>
              ) : "Continue"}
            </button>

            {formErr && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{formErr}</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">&copy; {new Date().getFullYear()} Input. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default function CrossfitWessex() {
  const [searchParams] = useSearchParams();
  const verified = searchParams.get("verified") === "1";
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const nav = useNavigate();

  // Store partnership tracking on mount
  useState(() => {
    localStorage.setItem("partner", "crossfit-wessex");
  });

  const sendCode = async () => {
    if (sending) return;
    setErr(null);

    if (!isEmail(email)) return setErr("Please enter a valid email address.");
    if (!agreedTerms) return setErr("Please agree to the terms to continue.");

    try {
      setSending(true);

      // 1. Determine signup vs login
      const res = await fetch(`${ENV.EDGE_BASE}/auth-email-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ENV.SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to start login");
      const mode = json.mode as "signup" | "login";

      // 2. Send OTP
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: mode === "signup" },
      });
      if (error) {
        if ((error as any).status === 429) {
          throw new Error("Too many attempts. Please wait a minute.");
        }
        throw error;
      }

      // 3. Activate 2-month free promo (fire-and-forget, don't block signup)
      fetch(`${ENV.EDGE_BASE}/crossfit-wessex-activate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ENV.SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email }),
      }).catch(() => {});

      localStorage.setItem("signup_email", email);
      localStorage.setItem("otp_type", mode);
      nav("/check-email?partner=crossfit-wessex");
    } catch (e: any) {
      setErr(e.message || "Failed to send code");
    } finally {
      setSending(false);
    }
  };

  // ─── Verified: About You form → Success screen ───
  if (verified) {
    return <CrossfitWessexOnboarding />;
  }

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#e2e8f0]">
        <div className="flex items-center justify-between px-4 py-3 max-w-screen-xl mx-auto">
          {/* Dual branding */}
          <div className="flex items-center gap-3">
            {/* Input logo */}
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-full bg-[#a78bfa] flex items-center justify-center">
                <svg className="w-4 h-4 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="font-bold text-[#0f172a] text-sm hidden sm:inline">Input</span>
            </div>

            {/* Divider */}
            <span className="text-gray-600 text-lg font-light">x</span>

            {/* CrossFit Wessex logo */}
            <div className="flex items-center gap-1.5">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center font-black text-[8px] text-[#0f172a]"
                style={{ background: CF_PURPLE }}
              >
                CFW
              </div>
              <span className="font-bold text-[#0f172a] text-sm hidden sm:inline">CrossFit Wessex</span>
            </div>
          </div>

          <a
            href="#signup"
            className="px-5 py-2 rounded-lg font-bold text-sm text-[#0f172a] transition hover:opacity-90"
            style={{ background: CF_PURPLE }}
          >
            Claim Free Access
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(ellipse at 30% 20%, ${CF_PURPLE}40, transparent 60%), radial-gradient(ellipse at 70% 80%, ${IPAI_GREEN}20, transparent 60%)`,
          }}
        />

        <div className="relative max-w-3xl mx-auto px-4 pt-16 pb-12 sm:pt-24 sm:pb-16 text-center">
          {/* Partnership badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8"
            style={{
              background: `${CF_PURPLE}15`,
              border: `1px solid ${CF_PURPLE}40`,
              color: CF_PURPLE,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: CF_PURPLE }} />
            CrossFit Wessex Members
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            <span style={{ color: CF_PURPLE }}>2 Months Free</span>
            <br />
            AI Coaching for{" "}
            <span style={{ color: IPAI_GREEN }}>CrossFit Wessex</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-500 max-w-xl mx-auto mb-4 leading-relaxed">
            As a CrossFit Wessex member, get{" "}
            <strong className="text-[#0f172a]">2 months of free access</strong> to
            Input — AI-powered training, nutrition, and recovery coaching
            personalised for you.
          </p>

          <p className="text-base text-gray-500 mb-10">
            No credit card required &middot; Just enter your email to get started
          </p>

          {/* INLINE SIGNUP FORM */}
          <div
            id="signup"
            className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5 sm:p-6 max-w-md mx-auto text-left"
          >
            <h2 className="text-lg font-bold text-[#0f172a] mb-1 text-center">
              Claim your 2 months free
            </h2>
            <p className="text-sm text-gray-500 mb-5 text-center">
              We'll send a login code to your email
            </p>

            {/* Email Input */}
            <div className="mb-4">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendCode()}
                className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] text-base placeholder-gray-600 focus:outline-none transition-all"
                style={{ borderColor: email ? `${CF_PURPLE}50` : undefined }}
                autoFocus
              />
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer group mb-5">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
                    agreedTerms
                      ? "border-transparent"
                      : "border-[#e2e8f0] group-hover:border-[#e2e8f0] bg-[#f1f5f9]"
                  }`}
                  style={agreedTerms ? { background: CF_PURPLE, borderColor: CF_PURPLE } : undefined}
                >
                  {agreedTerms && (
                    <svg className="w-3 h-3 text-[#0f172a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-500 leading-relaxed">
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#0f172a] hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" className="text-[#0f172a] hover:underline">
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* CTA Button */}
            <button
              onClick={sendCode}
              disabled={sending || !email || !agreedTerms}
              className="w-full h-12 font-semibold text-base rounded-xl transition-all duration-200 disabled:bg-[#f1f5f9] disabled:text-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-[#0f172a]"
              style={
                !sending && email && agreedTerms
                  ? { background: CF_PURPLE }
                  : undefined
              }
            >
              {sending ? (
                <>
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending code...
                </>
              ) : (
                "Get Free Access"
              )}
            </button>

            {err && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{err}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-20 px-4 border-t border-[#e2e8f0]" style={{ background: "rgba(248,250,252,0.6)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12">How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12">
            {[
              { step: "1", title: "Enter your email", desc: "Sign up above — we'll send a login code to your inbox" },
              { step: "2", title: "Download the app", desc: "Get Input from the App Store" },
              { step: "3", title: "Set up your profile", desc: "Tell us about you and get your personalised plan" },
            ].map((s) => (
              <div key={s.step} className="flex flex-col items-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-[#0f172a] mb-4"
                  style={{
                    background: `linear-gradient(135deg, ${CF_PURPLE}, ${IPAI_GREEN})`,
                    boxShadow: `0 8px 30px ${CF_PURPLE}40`,
                  }}
                >
                  {s.step}
                </div>
                <h3 className="font-semibold text-[#0f172a] text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT YOU PREVIEW */}
      <section className="py-16 sm:py-20 px-4 border-t border-[#e2e8f0]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text side */}
            <div className="text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Tell us <span style={{ color: IPAI_GREEN }}>about you</span>
              </h2>
              <p className="text-gray-500 leading-relaxed mb-6">
                Once you download the app, you'll fill in your profile — name,
                height, weight, and activity level. We use this to build your
                personalised training and nutrition plan.
              </p>
              <ul className="space-y-3 text-left inline-block">
                {[
                  "Personalised calorie & macro targets",
                  "AI training plans tailored to CrossFit",
                  "Recovery insights with Garmin & WHOOP",
                  "24/7 AI coach for daily support",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${IPAI_GREEN}20` }}>
                      <svg className="w-3 h-3" style={{ color: IPAI_GREEN }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Phone mockup side */}
            <div className="flex justify-center">
              <AboutYouMockup />
            </div>
          </div>
        </div>
      </section>

      {/* DOWNLOAD THE APP */}
      <section className="py-16 sm:py-20 px-4 border-t border-[#e2e8f0]" style={{ background: "rgba(248,250,252,0.6)" }}>
        <div
          className="max-w-2xl mx-auto rounded-2xl p-8 sm:p-10 text-center relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${CF_PURPLE}15, ${IPAI_GREEN}10)`,
            border: `1px solid ${CF_PURPLE}30`,
          }}
        >
          <div className="mb-6">
            <svg className="w-16 h-16 mx-auto text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="5" y="2" width="14" height="20" rx="3" />
              <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Download the App
          </h2>
          <p className="text-gray-500 mb-2 text-lg">
            Get Input on the App Store
          </p>
          <p className="text-gray-500 text-sm mb-8">
            Sign in with the same email you used above — your 2 months free access will be waiting for you.
          </p>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-3.5 rounded-xl font-bold text-base text-[#0f172a] transition hover:opacity-90"
            style={{ background: CF_PURPLE }}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            Download on the App Store
          </a>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-16 sm:py-20 px-4 border-t border-[#e2e8f0]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            What's included in your{" "}
            <span style={{ color: CF_PURPLE }}>free 2 months</span>
          </h2>
          <p className="text-gray-500 text-center mb-12 max-w-lg mx-auto">
            Full access to everything Input has to offer — no limits.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "AI Training Plans",
                desc: "Personalised programs built for CrossFit — strength, conditioning, and skill work.",
                color: IPAI_GREEN,
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Nutrition Coaching",
                desc: "AI meal plans and macro tracking to fuel your WODs and hit your body composition goals.",
                color: CF_PURPLE,
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Recovery Insights",
                desc: "Track sleep, HRV, and readiness with Garmin and WHOOP. Train smart, recover smarter.",
                color: "#a855f7",
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "24/7 AI Coach",
                desc: "Ask anything about training, nutrition, or recovery — get instant personalised advice.",
                color: "#3b82f6",
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Workout Tracking",
                desc: "Log every session with detailed metrics. Connect Bluetooth devices for real-time data.",
                color: IPAI_GREEN,
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="8.5" cy="7" r="4" />
                    <path d="M20 8v6M23 11h-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Personalised Plans",
                desc: "Everything adapts to your goals, fitness level, and schedule. No cookie-cutter programs.",
                color: "#eab308",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl border border-[#e2e8f0] bg-[#f8fafc] hover:border-[#e2e8f0] transition-all"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${feature.color}15`, color: feature.color }}
                >
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-[#0f172a] mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-12 sm:py-16 px-4 border-t border-[#e2e8f0]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Ready to level up your training?
          </h2>
          <p className="text-gray-500 mb-8 text-lg">
            2 months free — exclusive to CrossFit Wessex members.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#signup"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl font-bold text-base text-[#0f172a] transition hover:opacity-90"
              style={{ background: CF_PURPLE }}
            >
              Claim Free Access
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base text-[#0f172a] border border-[#e2e8f0] hover:border-[#e2e8f0] transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              Download App
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-4 border-t border-[#e2e8f0] text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full bg-[#a78bfa] flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="font-bold text-[#0f172a] text-sm">Input</span>
          </div>
          <span className="text-gray-600">x</span>
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full flex items-center justify-center font-black text-[7px] text-[#0f172a]" style={{ background: CF_PURPLE }}>
              CFW
            </div>
            <span className="font-bold text-[#0f172a] text-sm">CrossFit Wessex</span>
          </div>
        </div>
        <div className="flex gap-6 justify-center mb-4">
          <Link to="/terms" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Terms</Link>
          <Link to="/privacy" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Privacy</Link>
          <Link to="/contact" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Contact</Link>
        </div>
        <p className="text-gray-600 text-xs">
          &copy; {new Date().getFullYear()} Input. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
